import React from "react";

// Tailwind CSS required. Place in app/terms/page.tsx or pages/terms.tsx
// Matches the bilingual layout of your Privacy Policy page.

export default function TermsAndConditionsPage() {
  const [lang, setLang] = React.useState<"en" | "ar">("en");

  const Section: React.FC<{ id: string; title: string; children: React.ReactNode }> = ({ id, title, children }) => (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-2xl font-semibold mb-3">{title}</h2>
      <div className="prose max-w-none dark:prose-invert">{children}</div>
    </section>
  );

  const NavLink: React.FC<{ href: string; label: string }> = ({ href, label }) => (
    <a href={href} className="text-sm px-3 py-1 rounded-full border hover:bg-gray-50 dark:hover:bg-gray-800 transition">
      {label}
    </a>
  );

  return (
    <main className="min-h-screen bg-white dark:bg-neutral-950 text-neutral-900 dark:text-neutral-50">
      <div className="mx-auto max-w-4xl px-4 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Terms & Conditions</h1>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">Last updated: October 24, 2025</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm">Language:</span>
            <div className="inline-flex rounded-xl border overflow-hidden">
              <button
                onClick={() => setLang("en")}
                className={`px-3 py-1.5 text-sm ${lang === "en" ? "bg-neutral-900 text-white dark:bg-white dark:text-neutral-900" : "bg-transparent"}`}
              >EN</button>
              <button
                onClick={() => setLang("ar")}
                className={`px-3 py-1.5 text-sm ${lang === "ar" ? "bg-neutral-900 text-white dark:bg-white dark:text-neutral-900" : "bg-transparent"}`}
              >AR</button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[220px,1fr] gap-6">
          <aside className="md:sticky md:top-6 h-max border rounded-2xl p-3 md:p-4 bg-neutral-50 dark:bg-neutral-900/40">
            <nav className="flex flex-wrap md:flex-col gap-2">
              <NavLink href="#acceptance" label={lang === "en" ? "Acceptance" : "القبول"} />
              <NavLink href="#service" label={lang === "en" ? "Service" : "الخدمة"} />
              <NavLink href="#accounts" label={lang === "en" ? "Accounts" : "الحسابات"} />
              <NavLink href="#usage" label={lang === "en" ? "Usage Rules" : "قواعد الاستخدام"} />
              <NavLink href="#availability" label={lang === "en" ? "Availability" : "التوفر"} />
              <NavLink href="#data" label={lang === "en" ? "Data & Liability" : "البيانات والمسؤولية"} />
              <NavLink href="#payments" label={lang === "en" ? "Payments" : "المدفوعات"} />
              <NavLink href="#termination" label={lang === "en" ? "Termination" : "إنهاء الحساب"} />
              <NavLink href="#ip" label={lang === "en" ? "Intellectual Property" : "الملكية الفكرية"} />
              <NavLink href="#law" label={lang === "en" ? "Governing Law" : "القانون المعمول به"} />
              <NavLink href="#contact" label={lang === "en" ? "Contact" : "التواصل"} />
            </nav>
          </aside>

          <div className="flex flex-col gap-8">
            {lang === "en" ? (
              <div>
                <p className="text-base mb-6">
                  These Terms and Conditions ("Terms") govern your use of the <strong>Loyalty Stamper / Roaait</strong> SaaS platform operated by <strong>ROAA DATA IT W.L.L</strong> ("Company", "we", "us"). By creating an account or using the Service, you agree to these Terms.
                </p>

                <Section id="acceptance" title="1. Acceptance of Terms">
                  <p>By signing up or accessing the Service, you confirm that you are a legitimate business and have the authority to agree to these Terms. If you do not agree, do not use the Service.</p>
                </Section>

                <Section id="service" title="2. Description of Service">
                  <p>
                    Loyalty Stamper / Roaait provides a cloud-based loyalty management system allowing merchants to issue, scan, and manage digital loyalty cards integrated with Apple Wallet and Google Wallet.
                  </p>
                </Section>

                <Section id="accounts" title="3. Merchant Accounts">
                  <ul>
                    <li>You must provide accurate and complete registration information.</li>
                    <li>Fake, automated, or fraudulent signups are strictly prohibited.</li>
                    <li>We reserve the right to suspend or terminate accounts engaged in illegitimate or abusive activity.</li>
                  </ul>
                </Section>

                <Section id="usage" title="4. Acceptable Use">
                  <ul>
                    <li>Merchants are responsible for all content and data uploaded to their account.</li>
                    <li>Use of the Service for spam, illegal promotions, or misleading activities is forbidden.</li>
                    <li>Merchants must comply with all applicable privacy and marketing laws.</li>
                  </ul>
                </Section>

                <Section id="availability" title="5. Service Availability and Performance">
                  <p>
                    While we strive for reliability, the Service may experience slow responses or downtime due to maintenance, internet issues, or third-party providers. The Company is not liable for any service interruption, and no refunds are provided for downtime or performance issues.
                  </p>
                </Section>

                <Section id="data" title="6. Data, Security, and Liability">
                  <ul>
                    <li>The Company implements reasonable safeguards to protect data but is not responsible for any loss, corruption, or unauthorized access beyond our control.</li>
                    <li>Merchants should maintain their own backups of important data.</li>
                    <li>If the Company ceases operations, all active accounts may be closed, and stored data may be permanently deleted without compensation.</li>
                  </ul>
                </Section>

                <Section id="payments" title="7. Payments and Refunds">
                  <ul>
                    <li>All subscription payments are final and non-refundable unless required by law.</li>
                    <li>Pricing may be updated at any time with reasonable notice.</li>
                    <li>Service credits or refunds are not provided for partial periods or performance issues.</li>
                  </ul>
                </Section>

                <Section id="termination" title="8. Suspension and Termination">
                  <p>
                    We may suspend or terminate your account at our sole discretion for violations of these Terms, fraudulent activity, or any action that harms the integrity of the Service. Upon termination, your right to access the Service ceases immediately.
                  </p>
                </Section>

                <Section id="ip" title="9. Intellectual Property">
                  <p>
                    All rights, titles, and interests in the platform, trademarks, code, and materials belong to ROAA DATA IT W.L.L. Merchants retain ownership of their own business content uploaded to the Service.
                  </p>
                </Section>

                <Section id="law" title="10. Governing Law and Disputes">
                  <p>
                    These Terms are governed by the laws of the <strong>Kingdom of Bahrain</strong>. Any dispute shall be resolved by the competent courts of Bahrain or through arbitration if mutually agreed.
                  </p>
                </Section>

                <Section id="contact" title="11. Contact Information">
                  <p>
                    ROAA DATA IT W.L.L, Kingdom of Bahrain<br/>
                    Email: <a href="mailto:info@roaait.com">info@roaait.com</a><br/>
                    EU Representative: Muhammad Samad, Karhoksgatan 86, 55612 Jönköping, Sweden — <a href="mailto:info@roaait.com">info@roaait.com</a>
                  </p>
                </Section>
              </div>
            ) : (
              <div dir="rtl" className="text-right">
                <p className="text-base mb-6">
                  تحكم هذه الشروط والأحكام ("الشروط") استخدامك لمنصة <strong>Loyalty Stamper / Roaait</strong> التي تديرها <strong>شركة رؤى لتقنية المعلومات ذ.م.م</strong>. باستخدامك للخدمة أو إنشاء حساب، فإنك توافق على هذه الشروط.
                </p>

                <Section id="acceptance" title="1. القبول">
                  <p>بتسجيلك أو استخدامك للخدمة، فإنك تؤكد أنك تمثل نشاطًا تجاريًا مشروعًا ولديك الصلاحية للموافقة على هذه الشروط.</p>
                </Section>

                <Section id="service" title="2. وصف الخدمة">
                  <p>
                    تقدم منصة Loyalty Stamper / Roaait نظام ولاء سحابي يمكّن التجار من إصدار وإدارة بطاقات الولاء الرقمية المتكاملة مع Apple Wallet و Google Wallet.
                  </p>
                </Section>

                <Section id="accounts" title="3. الحسابات">
                  <ul>
                    <li>يجب تقديم معلومات دقيقة وكاملة عند التسجيل.</li>
                    <li>يُحظر تمامًا إنشاء حسابات وهمية أو آلية أو احتيالية.</li>
                    <li>يحتفظ مزود الخدمة بالحق في تعليق أو إنهاء أي حساب يثبت قيامه بنشاط غير مشروع أو مسيء.</li>
                  </ul>
                </Section>

                <Section id="usage" title="4. قواعد الاستخدام">
                  <ul>
                    <li>يتحمل التاجر المسؤولية الكاملة عن البيانات والمحتوى الذي يضيفه إلى حسابه.</li>
                    <li>يُحظر استخدام الخدمة في إرسال الرسائل العشوائية أو الأنشطة غير القانونية أو المضللة.</li>
                    <li>يجب على التجار الالتزام بجميع القوانين المتعلقة بالخصوصية والتسويق.</li>
                  </ul>
                </Section>

                <Section id="availability" title="5. التوفر والأداء">
                  <p>
                    نسعى لتوفير خدمة موثوقة، ولكن قد يحدث تباطؤ أو توقف مؤقت بسبب الصيانة أو مزودي الخدمة. لا تتحمل الشركة أي مسؤولية عن مثل هذه الانقطاعات ولا يتم تقديم أي مبالغ مستردة.
                  </p>
                </Section>

                <Section id="data" title="6. البيانات والمسؤولية">
                  <ul>
                    <li>تطبق الشركة تدابير أمنية معقولة لحماية البيانات، ولكنها غير مسؤولة عن أي فقدان أو تلف للبيانات خارج سيطرتها.</li>
                    <li>يُنصح التجار بالاحتفاظ بنسخ احتياطية خاصة بهم.</li>
                    <li>في حال توقف الشركة عن العمل، قد يتم إغلاق جميع الحسابات النشطة وحذف البيانات دون تعويض.</li>
                  </ul>
                </Section>

                <Section id="payments" title="7. المدفوعات والاسترداد">
                  <ul>
                    <li>جميع المدفوعات غير قابلة للاسترداد إلا إذا نص القانون على خلاف ذلك.</li>
                    <li>يجوز تعديل الأسعار أو الخطط مع إشعار مسبق.</li>
                    <li>لا يتم تقديم أي استرداد عن فترات جزئية أو بطء في الأداء.</li>
                  </ul>
                </Section>

                <Section id="termination" title="8. إنهاء الحساب">
                  <p>
                    يجوز لنا تعليق أو إنهاء حسابك وفقًا لتقديرنا في حال انتهاك هذه الشروط أو القيام بنشاط احتيالي. عند الإنهاء، يتوقف حقك في الوصول إلى الخدمة فورًا.
                  </p>
                </Section>

                <Section id="ip" title="9. الملكية الفكرية">
                  <p>
                    جميع الحقوق والعلامات التجارية والرموز والمواد تخص شركة رؤى لتقنية المعلومات ذ.م.م. ويحتفظ التاجر بحقوق المحتوى الذي يرفعه بنفسه.
                  </p>
                </Section>

                <Section id="law" title="10. القانون المعمول به والنزاعات">
                  <p>
                    تخضع هذه الشروط لقوانين <strong>مملكة البحرين</strong>، ويتم حل أي نزاع أمام المحاكم المختصة في البحرين أو عن طريق التحكيم باتفاق الطرفين.
                  </p>
                </Section>

                <Section id="contact" title="11. التواصل">
                  <p>
                    شركة رؤى لتقنية المعلومات ذ.م.م — مملكة البحرين<br/>
                    البريد الإلكتروني: <a href="mailto:info@roaait.com">info@roaait.com</a><br/>
                    ممثل الاتحاد الأوروبي: محمد سمد، كارهوكسغاتان 86، 55612 يونشوبينغ، السويد — <a href="mailto:info@roaait.com">info@roaait.com</a>
                  </p>
                </Section>
              </div>
            )}
          </div>
        </div>

        <div className="mt-10 text-xs text-neutral-500 dark:text-neutral-400">
          <p>These Terms are provided for transparency and do not constitute legal advice.</p>
        </div>
      </div>
    </main>
  );
}